/**
 * React Hooks for General Files
 *
 * This module provides custom hooks for managing general file operations
 * in React components using React Query.
 */

'use client';

import { useState } from 'react';
import {
  useQuery,
  useMutation,
  useQueryClient,
  UseQueryOptions,
  UseMutationOptions
} from '@tanstack/react-query';
import { toast } from '@/lib/toast';
import { uploadFileToUrl } from '@/lib/uploads';
import { gatedAction } from '@/lib/api/auth-gate';
import {
  getPresignedUrlAction,
  uploadCompleteAction,
  getFileAction,
  downloadFileAction
} from './actions';
import { generalFilesApi } from './client';
import { getDependencies } from '@/lib/services/query-dependencies';
import { fileKeys, documentKeys } from '@/lib/query-keys';
import { getFileCacheKey, validateFile } from './utils';
import type {
  FileEntity,
  UploadCompleteResponse,
  GetPresignedUrlRequest,
  PresignedUrlResponse,
  UpdateFileRequest,
  FileDownloadResponse,
  FileType
} from './types';

// ============================================================================
// Query Hooks
// ============================================================================

/**
 * Hook to fetch a single file's metadata
 */
export function useFile(
  fileId: number,
  options?: Omit<UseQueryOptions<FileEntity>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: getFileCacheKey(fileId),
    queryFn: () => gatedAction(() => getFileAction(fileId)),
    staleTime: 10 * 60 * 1000, // 10 minutes
    ...options
  });
}

// ============================================================================
// Mutation Hooks
// ============================================================================

/**
 * Hook for getting presigned upload URL
 */
export function usePresignedUrl(
  options?: UseMutationOptions<
    PresignedUrlResponse,
    Error,
    GetPresignedUrlRequest
  >
) {
  return useMutation({
    mutationFn: async (request: GetPresignedUrlRequest) => {
      const result = await getPresignedUrlAction(request);
      if (!result.success) {
        throw new Error(result.error || 'Failed to get upload URL');
      }
      return result.data!;
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to get upload URL');
    },
    ...options
  });
}

/**
 * Hook for completing file upload
 */
export function useUploadComplete(
  options?: UseMutationOptions<
    UploadCompleteResponse,
    Error,
    {
      fileId: number;
      originalFilename: string;
    }
  >
) {
  return useMutation({
    mutationFn: async ({ fileId, originalFilename }) => {
      const result = await uploadCompleteAction({ fileId, originalFilename });
      if (!result.success) {
        throw new Error(result.error || 'Failed to complete upload');
      }
      return result.data!;
    },
    // Deliberately no onSuccess cache write or toast. This endpoint returns an
    // acknowledgement (`{ fileId }`), not the file row — the previous version
    // wrote `getFileCacheKey(data.id)` with an undefined id and threw
    // "Cannot read properties of undefined (reading 'toString')". It is only
    // ever called as a step inside `useFileUpload`, which reads the row back and
    // owns the cache write and the single success toast.
    onError: (error) => {
      toast.error(error.message || 'Failed to complete upload');
    },
    ...options
  });
}

/**
 * Hook for complete file upload process (presigned URL + upload + complete)
 */
export function useFileUpload(
  options?: UseMutationOptions<
    FileEntity,
    Error,
    {
      file: File;
      fileType: FileType;
      isCertifiedCopy?: boolean;
      matterId?: number;
    }
  >
) {
  const queryClient = useQueryClient();
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const presignedUrlMutation = usePresignedUrl();
  const uploadCompleteMutation = useUploadComplete();

  const mutation = useMutation({
    mutationFn: async ({ file, fileType, isCertifiedCopy, matterId }) => {
      try {
        // Validate file
        const validation = validateFile(file, fileType);
        if (!validation.valid) {
          throw new Error(validation.error);
        }

        // Step 1: Get presigned URL (10% progress)
        setUploadProgress(10);
        const presignedResult = await presignedUrlMutation.mutateAsync({
          fileType,
          matterId
        });

        // Step 2: real S3 PUT via the shared primitive (10–90% progress).
        // Previously this SIMULATED progress and the actual PUT was commented
        // out — general-file uploads never reached S3. LF-4771 wires it for real.
        setUploadProgress(20);
        await uploadFileToUrl(presignedResult.presignedUrl, file, {
          contentType: file.type,
          onProgress: (p) =>
            setUploadProgress(20 + Math.round((p.percent / 100) * 70))
        });

        // Step 3: Complete upload (90-100% progress)
        setUploadProgress(95);
        await uploadCompleteMutation.mutateAsync({
          fileId: presignedResult.fileId,
          originalFilename: file.name
        });

        // `POST /files/:id/upload-complete` answers with `UploadCompleteResponseDto`
        // — `{ fileId, fieldClassificationReport? }` — NOT the file row, even
        // though the action types it as `FileEntity`. Consumers (and this hook's
        // own onSuccess) read `.id` / `.matterId` / `.originalFilename`, so
        // returning that response directly handed them `undefined` and threw
        // "Cannot read properties of undefined (reading 'toString')" in
        // `getFileCacheKey`. Read the row back so the declared contract holds.
        const fileResult = await getFileAction(presignedResult.fileId);
        if (!fileResult.success || !fileResult.data) {
          throw new Error(
            fileResult.error ??
              'The file uploaded, but we could not read it back.'
          );
        }

        const uploaded =
          isCertifiedCopy !== undefined
            ? await generalFilesApi.updateFile(fileResult.data.id, {
                isCertifiedCopy
              })
            : fileResult.data;

        setUploadProgress(100);
        return uploaded;
      } finally {
        // Reset progress after a delay
        setTimeout(() => setUploadProgress(0), 1000);
      }
    },
    onSuccess: (data) => {
      // Update cache
      queryClient.setQueryData(getFileCacheKey(data.id), data);

      // Invalidate related queries if file is associated with a matter
      if (data.matterId) {
        queryClient.invalidateQueries({
          queryKey: fileKeys.forMatter(data.matterId)
        });
      }

      toast.success(`File "${data.originalFilename}" uploaded successfully`);
    },
    onError: (error) => {
      // Keep the stack: a toast alone leaves a failed upload undebuggable, and
      // this chain spans a presign, an S3 PUT, an upload-complete and a read-back.
      console.error('[general-files] upload failed', error);
      toast.error(error.message || 'Failed to upload file');
    },
    ...options
  });

  return {
    ...mutation,
    uploadProgress
  };
}

/**
 * Hook to update file metadata
 */
export function useUpdateFile(
  options?: UseMutationOptions<
    FileEntity,
    Error,
    {
      fileId: number;
      updates: UpdateFileRequest;
    }
  >
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ fileId, updates }) => {
      return generalFilesApi.updateFile(fileId, updates);
    },
    onSuccess: (data, { fileId }) => {
      // Update cache
      queryClient.setQueryData(getFileCacheKey(fileId), data);

      // Cascade invalidation through the single source of truth.
      if (data.matterId) {
        const keys = getDependencies('document', data.matterId, fileId);
        keys.forEach((queryKey) =>
          queryClient.invalidateQueries({ queryKey, exact: false })
        );
      } else {
        queryClient.invalidateQueries({ queryKey: fileKeys.all });
      }

      toast.success('File updated successfully');
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to update file');
    },
    ...options
  });
}

/**
 * Hook to delete a file
 */
export function useDeleteFile(
  options?: UseMutationOptions<void, Error, number>
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (fileId: number) => {
      await generalFilesApi.deleteFile(fileId);
    },
    onSuccess: (_, fileId) => {
      // Remove from cache
      queryClient.removeQueries({
        queryKey: getFileCacheKey(fileId)
      });

      // The general-files delete path is not matter-aware (the soft-delete
      // endpoint takes only a fileId), so invalidate the matter-scoped
      // file caches broadly. If a caller knows the matterId, the document
      // resolver can be used directly.
      queryClient.invalidateQueries({ queryKey: fileKeys.all });
      queryClient.invalidateQueries({ queryKey: fileKeys.lists() });
      queryClient.invalidateQueries({ queryKey: documentKeys.all });

      toast.success('File deleted successfully');
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to delete file');
    },
    ...options
  });
}

/**
 * Hook to download a file
 */
export function useFileDownload(
  fileId: number,
  options?: UseMutationOptions<FileDownloadResponse, Error, void>
) {
  return useMutation({
    mutationFn: async () => {
      const result = await downloadFileAction(fileId);
      if (!result.success) {
        throw new Error(result.error || 'Failed to get download URL');
      }
      return result.data!;
    },
    onSuccess: (data) => {
      // Open download URL in new tab
      window.open(data.downloadUrl, '_blank');
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to download file');
    },
    ...options
  });
}
